import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { User, Lock, Save, Eye, EyeOff, Check, X } from 'lucide-react';
import { apiErrorMessage } from '../lib/utils';
import { isValidName, isValidMobile, checkPasswordRules } from '../lib/registrationValidation';

const ProfilePage = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    mobile_number: user?.mobile_number || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleProfileChange = (e) => {
    setProfileData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.first_name.trim() || !profileData.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    // Same rules as registration (lib/registrationValidation.js). These checks are
    // UX only: the backend's UserUpdate enforces the same rules.
    if (!isValidName(profileData.first_name)) {
      toast.error('Please enter a valid first name');
      return;
    }
    if (!isValidName(profileData.last_name)) {
      toast.error('Please enter a valid last name');
      return;
    }
    if (!isValidMobile(profileData.mobile_number)) {
      toast.error('Please enter a valid mobile number (e.g., +1234567890)');
      return;
    }

    setLoadingProfile(true);
    try {
      await updateProfile({
        first_name: profileData.first_name.trim(),
        last_name: profileData.last_name.trim(),
        mobile_number: profileData.mobile_number.trim() || null
      });
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error(apiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setLoadingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    // Same 5 rules as registration (UX only -- the backend's ChangePassword
    // model enforces them too).
    if (!checkPasswordRules(passwordData.new_password).every((r) => r.met)) {
      toast.error('New password does not meet all the requirements listed below it');
      return;
    }

    if (passwordData.new_password === passwordData.current_password) {
      toast.error('New password must be different from your current password');
      return;
    }

    setLoadingPassword(true);
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      console.error('Password change error:', err);
      toast.error(apiErrorMessage(err, 'Failed to change password'));
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="profile-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profile Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 bg-chassis rounded-full flex items-center justify-center mb-4">
            <User className="h-10 w-10 text-white" />
          </div>
          <h1 className="font-chivo text-3xl font-black uppercase tracking-tight">
            {user?.first_name} {user?.last_name}
          </h1>
          <p className="text-muted-foreground font-mono text-sm">{user?.email}</p>
        </div>

        {/* Profile Information */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="font-chivo text-xl uppercase tracking-tight flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={profileData.first_name}
                    onChange={handleProfileChange}
                    className="h-12 border-2"
                    data-testid="profile-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={profileData.last_name}
                    onChange={handleProfileChange}
                    className="h-12 border-2"
                    data-testid="profile-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (Read-only)</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="h-12 border-2 bg-muted"
                  data-testid="profile-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile_number">Mobile Number</Label>
                <Input
                  id="mobile_number"
                  name="mobile_number"
                  type="tel"
                  value={profileData.mobile_number}
                  onChange={handleProfileChange}
                  placeholder="+1234567890"
                  className="h-12 border-2"
                  data-testid="profile-mobile-input"
                />
              </div>
              <Button 
                type="submit" 
                className="bg-chassis hover:bg-chassis/90 font-bold uppercase tracking-wider"
                disabled={loadingProfile}
                data-testid="profile-save-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {loadingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="font-chivo text-xl uppercase tracking-tight flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    className="h-12 border-2 pr-10"
                    data-testid="profile-current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Min 8 characters"
                    aria-describedby="new-password-rules"
                    className="h-12 border-2 pr-10"
                    data-testid="profile-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {/* Live rules checklist, same rules and look as the registration form. */}
                <ul id="new-password-rules" className="space-y-1 pt-1 text-xs" data-testid="profile-password-rules">
                  {checkPasswordRules(passwordData.new_password).map((rule) => (
                    <li
                      key={rule.id}
                      data-met={rule.met}
                      className={`flex items-start gap-1.5 ${rule.met ? 'text-emerald-700' : 'text-muted-foreground'}`}
                    >
                      {rule.met
                        ? <Check className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />
                        : <X className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />}
                      <span>
                        {rule.label}
                        <span className="sr-only">{rule.met ? ' (met)' : ' (not met)'}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  className="h-12 border-2"
                  data-testid="profile-confirm-password-input"
                />
              </div>
              <Button 
                type="submit" 
                variant="outline"
                className="border-2 font-bold uppercase tracking-wider"
                disabled={loadingPassword}
                data-testid="profile-change-password-btn"
              >
                <Lock className="h-4 w-4 mr-2" />
                {loadingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
